import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { CustomFormValidators } from "@app/directives/_forms/form-validation.service";
import { DateService } from "@app/services/date.service";
import { TranslationService } from "@app/services/translation.service";
import { VAutocompleteComponent } from "@directives/_forms/v-autocomplete/v-autocomplete.component";
import { ICombinedPreferences } from "@interfaces/ICombinedPreferences";
import { IJourney } from "@interfaces/IJourney";
import { IVehicle } from "@interfaces/IVehicle";
import { DynamicReportService } from "@reports/_services/dynamicReports.service";
import { ChartDefinition } from "@reports/enums/ChartDefinition";
import { IDataSourceDefinition } from "@reports/models/IDataSourceDefinition";
import { IColumnHeader } from "@reports/models/IColumnHeader";
import { IReportDefinition } from "@reports/models/IReportDefinition";
import { JourneyService } from "@services/journey.service";
import { PreferencesService } from "@services/preferences.service";
import { VehicleService } from "@services/vehicle.service";
import { copy } from "angular";
import { IRowDataSourceChange } from "../row-data-source-selector2/row-data-source-selector2.component";
import "./report-editor.component.scss";
import { NavigationService } from "@app/services/navigation.service";
import { ReportsNavigationModule } from "../../reports.navigation";

export interface IReportEditorSave {
    savedReport: IReportDefinition;
    savedAsCopy?: boolean;
    deleted?: boolean;
}

@Component({
    selector: "report-editor",
    template: require("./report-editor.component.html")
})
export class ReportEditorComponent implements OnInit, OnChanges {

    showDebugButtons: boolean = true;

    @Input() reportDefinition: IReportDefinition;
    @Input() reportId: number;
    @Input() hideDate: boolean;

    @Output() validate = new EventEmitter<boolean>();
    @Output() save = new EventEmitter<IReportEditorSave>();

    @ViewChild("columnHeaderAutocomplete", { static: false }) columnHeaderAutocomplete: VAutocompleteComponent<IColumnHeader>;


    // ---

    reports: IReportDefinition[];

    loading = false;
    reportEditorForm: FormGroup;

    allColumnHeaders: IColumnHeader[] = [];
    availableColumnHeaders: IColumnHeader[] = [];
    availableDataSources: IDataSourceDefinition[] = [];
    remainingAvailableColumns: IColumnHeader[];

    reportDataSources: IDataSourceDefinition[] = [];
    headersForReportDataSource: IColumnHeader[] = [];

    vehicles: IVehicle[];
    journeys: IJourney[];
    combinedPreferences: ICombinedPreferences;
    vehicleWithMostJourneys: IVehicle;

    rowTitleHeader: IColumnHeader;

    previewPdfUrl: string;
    gettingData: boolean;
    loadingPreview: boolean;
    saving: boolean;

    showTemperatureChartCheckBox: boolean;
    showDataSources: boolean;

    primaryDataSource: string;
    secondaryDataSource: string;

    readonly saveAsCopySuffix: string = " (Copy)";

    get sortHeaderId(): number {
        return this.reportEditorForm?.get("sortHeaderId").value;
    }

    get sortAscending(): boolean {
        return this.reportEditorForm?.get("sortAscending").value;
    }

    constructor(
        private readonly dynamicReportService: DynamicReportService,
        private readonly formBuilder: FormBuilder,
        private readonly vehicleService: VehicleService,
        private readonly journeyService: JourneyService,
        private readonly preferencesService: PreferencesService,
        private readonly dateService: DateService,
        private readonly translationService: TranslationService,
        private readonly navigationService: NavigationService
    ) {
    }

    ngOnInit(): void {
        this.prepare();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["report"]) {

            this.reportDefinition = copy(this.reportDefinition);

            this.dynamicReportService.getDataSourceDefinitionsAndMapToColumns(this.reportDefinition.TableDefinition.Headers);
        }
    }

    saveReport(saveAsCopy: boolean): void {
        if (!this.validateModelExists() || !this.validateSaveProgress()) {
            return;
        }

        this.saving = true;

        if (saveAsCopy) {
            this.dynamicReportService.clearParams(this.reportDefinition);
            this.setSaveAsCopyReportName();
        }

        this.reportDefinition.isSystemReport = false;

        this.dynamicReportService.saveReportDefinition(this.reportDefinition).then((result: IReportDefinition) => {
            this.save.emit({
                savedReport: result,
                savedAsCopy: saveAsCopy,
                deleted: !this.reportDefinition.Active
            });
        }).finally(() => {
            this.saving = false;


            this.navigationService.go(ReportsNavigationModule.ReportList);
        });
    }

    deleteReport() {
        this.reportDefinition.Active = false;

        this.saveReport(false);
    }

    previewReport(): void {
        this.loadingPreview = true;

        const previewReportModel = this.createReportPreviewModel(this.reportDefinition);

        this.dynamicReportService
            .populateReport(previewReportModel, true)
            .then(reportLog => {
                window.open(reportLog.pdfUrl, '_blank')?.focus();
            })
            .catch(error => {
                console.log("previewReport error", error);
            })
            .finally(() => {
                this.loadingPreview = false;
            });
    }

    prepare(): void {
        if (!this.reportDefinition) {
            this.setupNewReport();
        }

        this.reportDefinition = copy(this.reportDefinition);

        if (this.gettingData) {
            return;
        }

        this.getData().then(() => {

            if (!this.reportDefinition.TableDefinition.Headers?.find(header => header.IsRowTitle)) {
                this.dynamicReportService.setReportDefinitionRowTitle(this.reportDefinition, this.rowTitleHeader);
            }
            this.dynamicReportService.setReportDefinitionColumnIds(this.reportDefinition);
            this.dynamicReportService.ensureUniqueReportColumnHeadersIds(this.reportDefinition);
            this.dynamicReportService.ensureUniqueColumnHeadersIds(this.availableColumnHeaders);
            this.dynamicReportService.updateFilterOptionsForColumnHeaders(this.reportDefinition.TableDefinition.Headers, true);
            this.dynamicReportService.updateFilterOptionsForColumnHeaders(this.availableColumnHeaders);

            this.checkForTemperatureChartColumns();

            this.createForm();
            this.validateForm();
        });
    }

    onSortColumnHeaderChanged(columnHeader: IColumnHeader): void {
        this.checkForTemperatureChartColumns();
        this.reportEditorForm.get("sortHeaderId").setValue(columnHeader.Id);
        this.updateSortedHeader();
    }

    onSortOrderChanged(ascending: boolean): void {
        this.checkForTemperatureChartColumns();
        this.reportEditorForm.get("sortAscending").setValue(ascending);
        this.updateSortedHeader();
    }

    onPrimaryDataSourceChanged(data: string): void {
        this.primaryDataSource = data;
        this.selectNewDataSources(data, this.getCurrentDataSources()[1]);
        this.filterHeadersByReportDataSource();
    }

    onSecondaryDataSourceChanged(data: string): void {
        this.secondaryDataSource = data;
        this.selectNewDataSources(this.getCurrentDataSources()[0], data);
        this.filterHeadersByReportDataSource();
    }

    updateSortedHeader(): void {
        if (!this.reportDefinition?.TableDefinition?.Headers?.length) {
            return;
        }

        this.reportDefinition.TableDefinition.Headers.forEach(header => {
            header.Sorted = header.Id === this.sortHeaderId;

            if (header.Sorted) {
                header.SortAscending = this.sortAscending;
            }
        });
    }

    onColumnHeadersUpdated(columnHeaders: IColumnHeader[]): void {
        this.checkForTemperatureChartColumns();
        this.validateForm();
    }

    validateForm(): void {
        this.validate.emit(this.reportEditorForm.valid);
    }

    onRowDataSourceChanged(change: IRowDataSourceChange): void {
        if (!this.reportDefinition) {
            return;
        }

        const rowDataSourceChanged = this.reportDefinition.RowDataSource !== change.row;

        this.reportDefinition.RowDataSource = change.row;
        this.reportDefinition.SubRowDataSource = change.subRow;

        if (!rowDataSourceChanged) {
            return;
        }

        this.dynamicReportService.setReportDefinitionRowTitle(this.reportDefinition, this.rowTitleHeader);

        const titleRowId: number = this.dynamicReportService.findRowTitleColumn(this.reportDefinition.TableDefinition.Headers).Id;

        this.reportEditorForm?.get("sortHeaderId")?.setValue(titleRowId);

        this.updateSortedHeader();
    }

    onTemperatureChartsToggleChanged(value: boolean): void {
        this.updateChartsSettings(value);
    }

    onColumnsTabSelected(): void {
        this.showDataSources = true;
    }

    // ---

    private validateModelExists(): boolean {
        if (!this.reportDefinition) {
            console.warn("Tried to save a report definition when no model exists");
            return false;
        }
        return true;
    }

    private validateSaveProgress(): boolean {
        if (this.saving) {
            console.warn("Report already being saved");
            return false;
        }
        return true;
    }

    private setupNewReport(): void {
        this.reportDefinition = this.dynamicReportService.createReportTemplate();
    }

    private getData(): Promise<void> {
        this.gettingData = true;

        const promises: Promise<any>[] = [
            this.dynamicReportService.getDisplayColumnHeaders().then(columnHeaders => this.allColumnHeaders = columnHeaders) as Promise<IColumnHeader[]>,
            this.dynamicReportService.getDataSourceDefinitions().then(definitions => this.availableDataSources = definitions) as Promise<IDataSourceDefinition[]>,
            this.dynamicReportService.getReports().then(reports => this.reports = reports) as Promise<IReportDefinition[]>
        ];

        promises.push(this.loadCombinedPreferences());
        promises.push(this.loadVehicles());
        promises.push(this.loadJourneys());

        return Promise.all(promises).then(() => {
            this.findAndSetVehicleWithMostJourneys(this.vehicles, this.journeys);

            this.availableColumnHeaders = this.dynamicReportService.filterAvailableColumnHeaders(this.allColumnHeaders);
            this.rowTitleHeader = this.dynamicReportService.findRowTitleColumn(this.allColumnHeaders);

            this.renderDataSourceFiltering();

        }).finally(() => {
            this.gettingData = false;
        });
    }

    private renderDataSourceFiltering(): void {
        this.detectReportDataSources();
        this.filterHeadersByReportDataSource();
    }

    private loadCombinedPreferences(): Promise<void> {
        return (this.preferencesService.getAllPreferences() as Promise<ICombinedPreferences>).then(combinedPreferences => {
            this.combinedPreferences = combinedPreferences;
        });
    }

    private loadVehicles(): Promise<void> {
        return (this.vehicleService.getVehicles({ includePeripheralMappings: true }) as Promise<IVehicle[]>).then(vehicles => {
            this.vehicles = vehicles;
        });
    }

    private loadJourneys(): Promise<void> {
        const startDate = this.dateService.getStartOfDay(new Date());
        const endDate = this.dateService.getEndOfDay(new Date());

        return (this.journeyService.getAllJourneys(startDate, endDate) as Promise<IJourney[]>).then(journeys => {
            this.journeys = journeys;
        });
    }

    private createForm(): void {
        const sortSettings = this.dynamicReportService.getSortSettings(this.reportDefinition);

        this.reportEditorForm = this.formBuilder.group({
            reportName: [this.reportDefinition?.Title, [Validators.required, Validators.minLength(5), Validators.maxLength(50), CustomFormValidators.emptyWhiteSpace, this.uniqueReportNameValidator()]],
            sortHeaderId: [sortSettings.headerId, [Validators.required]],
            sortAscending: [sortSettings.ascending || false, [Validators.required]]
        });

        this.reportEditorForm.statusChanges.subscribe(x => {
            this.validateForm();
        });
    }

    private setSaveAsCopyReportName(): void {
        let proposedReportName = `${this.reportDefinition.Title}`

        while (this.reportNameInUse(proposedReportName)) {
            proposedReportName = `${proposedReportName}${this.saveAsCopySuffix}`
        }

        this.reportDefinition.Title = proposedReportName;
    }

    private uniqueReportNameValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {

            if (!this.reports?.length || !control.value) {
                return null;
            }

            const lowerCaseScheduleName = control.value.toLowerCase();

            const isDuplicate = this.reportNameInUse(lowerCaseScheduleName);

            return isDuplicate ? { customMessage: this.translationService.t("ReportEditor.UniqueReportNameWarning") } : null;
        };
    }

    private reportNameInUse(reportName: string): boolean {
        if (!reportName) {
            return false;
        }

        const matchingReports = this.reports.filter(report => {
            return report.Title.toLowerCase() === reportName.toLowerCase()
                && report.Id !== this.reportDefinition.Id;
        });

        return matchingReports?.length > 0;
    }

    private createReportPreviewModel(report: IReportDefinition): IReportDefinition {
        if (!report) {
            console.warn("No report definiton provided for preview model");
            return;
        }

        const copiedReport = copy(this.reportDefinition);

        copiedReport.Parameters = {
            Start: this.dateService.getStartOfDay(new Date()),
            End: this.dateService.getEndOfDay(new Date()),
        };

        copiedReport.ShowCharts = false;

        this.updateChartsSettings(this.reportDefinition.ShowCharts);

        if (this.vehicleWithMostJourneys && this.dynamicReportService.restrictReportToSingleResource(copiedReport)) {
            copiedReport.Parameters.VehicleId = this.vehicleWithMostJourneys.Id;
        }

        if (this.combinedPreferences) {
            copiedReport.DistanceMeasurement = this.combinedPreferences.DistanceMeasurement.Value;
            copiedReport.VolumeMeasurement = this.combinedPreferences.VolumeMeasurement.Value;
            copiedReport.TemperatureMeasurement = this.combinedPreferences.TemperatureMeasurement.Value;
            copiedReport.Currency = this.combinedPreferences.Currency.Value
        }

        return copiedReport;
    }

    private updateChartsSettings(showTemperatureCharts: boolean = false): void {
        if (showTemperatureCharts && this.showTemperatureChartCheckBox) {
            this.reportDefinition.ChartDefinition = ChartDefinition.Temperature;
        }

        this.reportDefinition.ShowCharts = showTemperatureCharts;
    }

    private findAndSetVehicleWithMostJourneys(vehicles: IVehicle[], journeys: IJourney[]) {
        if (!vehicles?.length || !journeys?.length) {
            return;
        }

        var currentMaxCount = 0;

        vehicles.forEach(vehicle => {
            vehicle.rangeJourneyCount = 0;

            journeys.forEach(journey => {
                if (journey.VehicleId === vehicle.Id) {
                    vehicle.rangeJourneyCount++;
                }
            });

            if (vehicle.rangeJourneyCount > currentMaxCount) {
                this.vehicleWithMostJourneys = vehicle;
            }
        });
    }

    private checkForTemperatureChartColumns(): void {
        this.showTemperatureChartCheckBox = this.dynamicReportService.findColumn("ProbeTemperatures", "Temperature", this.reportDefinition.TableDefinition.Headers) != null;

        if (!this.showTemperatureChartCheckBox) {
            this.reportDefinition.ShowCharts = false;
        }
    }

    private getCurrentDataSources(): string[] {
        // Map data sources in report
        const res = this.reportDefinition.TableDefinition.Headers.reduce((obj, v) => {
            obj[v.dataSourceDefinition.DataSourceType] = (obj[v.dataSourceDefinition.DataSourceType] || 0) + 1;
            return obj;
        }, {});

        let highestCount = 0;
        let highestKey: string;

        let highestCount2 = 0;
        let highestKey2: string;

        // Count data sources in report
        Object.keys(res).forEach((key, index) => {

            if (key == "SharedReportDataSource") {
                return;
            }

            if (highestCount < res[key]) {
                highestCount2 = highestCount;
                highestKey2 = highestKey;

                highestCount = res[key];
                highestKey = key;
            }
            else if (highestCount2 < res[key]) {
                highestCount2 = res[key];
                highestKey2 = key;
            }
        });

        if (!highestKey) {
            highestKey = this.primaryDataSource;
        }

        if (!highestKey2) {
            highestKey2 = this.secondaryDataSource;
        }

        return [highestKey, highestKey2];
    }

    private detectReportDataSources() {
        const dataSourceKeys: string[] = this.getCurrentDataSources();

        this.selectNewDataSources(dataSourceKeys[0], dataSourceKeys[1]);
    }

    private selectNewDataSources(dataSourceKey1: string, dataSourceKey2: string): void {
        this.reportDataSources = [];

        // Find matching data source to data source keys
        var highestSource = this.availableDataSources.find(s => s.DataSourceType == dataSourceKey1);
        var highestSource2 = this.availableDataSources.find(s => s.DataSourceType == dataSourceKey2);

        if (highestSource) {
            this.reportDataSources.push(highestSource);
        }

        if (highestSource2 && highestSource != highestSource2) {
            this.reportDataSources.push(highestSource2);
        }
    }

    private filterHeadersByReportDataSource() {
        this.headersForReportDataSource = this.availableColumnHeaders.filter(h => {

            if (h.dataSourceDefinition.DataSourceType == "SharedReportDataSource") {
                return true;
            }

            if (!this.reportDataSources?.length) {
                return false;
            }

            if (h.dataSourceDefinition.DataSourceType == this.reportDataSources[0].DataSourceType) {
                return true;
            }

            if (this.reportDataSources.length == 2 && h.dataSourceDefinition.DataSourceType == this.reportDataSources[1].DataSourceType) {
                return true;
            }

            return false;
        });
    }
}
